import { useCallback, useEffect, useState } from "react";
import {
  ensureCipherIdentity,
  ensureChatCipher,
  ensureGroupCipher,
  enableGroupCipher as enableGroup,
} from "./cipherlock";
import { notifyUnlocked } from "./cipherHeal";

// Manages this device's Cipher Lock for a conversation.
// - DM (mode "dm"): publishes identity + establishes/adopts the 1:1 PQXDH session (unwraps my
//   per-device wraps + heals other devices, cc_20). `refreshCipher({ healOnly })` never mints.
// - Circle (mode "group"): unwraps/heals the bloq group key (owner can enable it).
// Returns the live cipher state (active/pending/waiting/available/off + sessionId + pair code).
export function useChatCipher(userId, chatId, { enabled = true, mode = "dm", bloqId } = {}) {
  const [cipher, setCipher] = useState(null);

  const refresh = useCallback(async (opts = {}) => {
    if (!enabled || !userId) return;
    try {
      await ensureCipherIdentity(userId);
      let next;
      if (mode === "group" && bloqId) next = await ensureGroupCipher(userId, bloqId);
      else if (chatId) next = await ensureChatCipher(userId, chatId, { healOnly: !!opts.healOnly });
      if (!next) return;
      if (next.unlocked) notifyUnlocked(next.unlocked);
      setCipher(next);
    } catch {
      setCipher({ state: "off" });
    }
  }, [enabled, userId, chatId, mode, bloqId]);

  useEffect(() => {
    setCipher(null);
    refresh();
  }, [refresh]);

  const enableGroupCipher = useCallback(async () => {
    if (mode !== "group" || !bloqId || !userId) return;
    setCipher(await enableGroup(userId, bloqId));
  }, [mode, bloqId, userId]);

  return { cipher, refreshCipher: refresh, enableGroupCipher };
}
